import React, { useState } from 'react';
import styled from '@emotion/styled';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import { Fonts, Spacings } from '@styles/index';
import P1 from '@base/P1/P1';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { getIsGTPGoal } from '@api/selectors/constants';
import { segment } from '@actions/analytics';
import { SYLLABUS_MENU_CLICKED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { IChapterInfo } from 'types/syllabus';
import useConstants from '@api/hooks/useConstants';
import TopicTooltip, { ITopicTooltipFn } from '../CommonCards/TopicTooltip';

interface IStyleProps {
  $fontWeight: string;
  $cursor: string;
}

interface ISyllabusSideNavProps {
  chaptersList: IChapterInfo[];
  goalUID: string;
  areMoreCoursesAvailable: boolean;
  scrollToCourses: () => void;
  scrollToChapter: (index: number) => void;
  isSyllabusPageEducatorSpecific: boolean;
}

const maxNoOfChapters = 8;
const maxHeight = maxNoOfChapters * 18 + maxNoOfChapters * 16;

const Wrapper = styled.div`
  position: relative;
`;

const InnerWrapper = styled.div`
  position: sticky;
  top: 200px;
  margin-top: ${Spacings.SPACING_10B};
`;

const ChaptersList = styled.div`
  max-height: ${maxHeight}px;
  overflow-y: scroll;
  overflow-x: hidden;
  display: grid;
  margin-top: ${Spacings.SPACING_4B};
  padding-left: ${Spacings.SPACING_12B};
  &::-webkit-scrollbar {
    background-color: transparent;
    width: 0px;
  }
  &:hover {
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: ${Spacings.SPACING_10B};
      background-color: var(--color-text-secondary);
    }
  }
`;

const StyP1 = styled(P1)<IStyleProps>`
  line-height: 18px;
  margin-bottom: ${Spacings.SPACING_4B};
  max-width: 195px;
  text-overflow: ellipsis;
  cursor: ${({ $cursor }) => $cursor};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  font-weight: ${({ $fontWeight }) => $fontWeight};
`;

const StyH4 = styled(H4)`
  line-height: ${Spacings.SPACING_6B};
  font-weight: 600;
  padding-left: ${Spacings.SPACING_12B};
`;

const StyH5 = styled(H5)`
  cursor: pointer;
`;

const StyleH5 = styled(H5)`
  cursor: pointer;
  margin-top: ${Spacings.SPACING_2B};
  padding-left: ${Spacings.SPACING_12B};
`;

const CoursesWrapper = styled.div`
  height: ${Spacings.SPACING_12B};
  background: var(--color-base-0);
  display: flex;
  align-items: center;
  padding-left: ${Spacings.SPACING_12B};
  margin-right: 43px;
  box-shadow: ${({ color }) =>
    boxShadowGenerator({
      yOffset: '-16px',
      color
    })};
`;

export const TooltipWrapper = styled.div`
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-i-gray-v2);
  border-radius: ${Spacings.SPACING_1B};
  padding: ${Spacings.SPACING_1B};
`;

export const Text = styled.p`
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 100%;
  text-transform: uppercase;
  color: var(--color-text-primary);
`;

const StyPopper = styled(TopicTooltip)`
  margin-top: 10.5px;
`;

const SideNav: React.FC<ISyllabusSideNavProps> = ({
  chaptersList,
  goalUID,
  areMoreCoursesAvailable,
  scrollToCourses,
  scrollToChapter,
  isSyllabusPageEducatorSpecific
}) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const goalSegmentData = useGoalEventProps({ goalUID });
  const popupRef = React.useRef<ITopicTooltipFn>();

  const [selectedItem, setSelectedItem] = useState(-1);
  const onChapterLeave = () => {
    popupRef.current.onChapterLeave();
  };
  const onChapterHover = (educatorCoveredTopic) => (event) => {
    popupRef.current.onChapterHover(event, educatorCoveredTopic);
  };

  const renderCoursesNavItem = () => {
    if (chaptersList.length <= maxNoOfChapters) {
      return <StyleH5 onClick={scrollToCourses}>Courses</StyleH5>;
    }

    return (
      <CoursesWrapper
        color={
          isDarkModeOn ? 'rgba(66, 66, 68, 0.2)' : 'rgba(233, 238, 242, 0.4)'
        }
      >
        <StyH5 onClick={scrollToCourses}>Courses</StyH5>
      </CoursesWrapper>
    );
  };

  const onChapterClick = (index, educatorCoveredConcept) => () => {
    if (educatorCoveredConcept) {
      setSelectedItem(index);
      scrollToChapter(index);
      setTimeout(() => {
        setSelectedItem(-1);
      }, 2000);
      const conceptData = chaptersList[index];
      segment.track(SYLLABUS_MENU_CLICKED, {
        ...goalSegmentData,
        topology_uid: conceptData.uid,
        topology_name: conceptData.title,
        topology_level: 20
      });
    }
  };

  return (
    <Wrapper>
      <InnerWrapper>
        <div>
          <StyH4>{isGTP ? 'Concepts' : 'Chapters'}</StyH4>
          <ChaptersList>
            {chaptersList.map((chapter, index) => {
              const {
                uid,
                title,
                educatorCoveredConcept = true
              } = chapter || {};
              return (
                <StyP1
                  key={uid}
                  color="var(--color-text-primary)"
                  onClick={onChapterClick(index, educatorCoveredConcept)}
                  onMouseEnter={onChapterHover(educatorCoveredConcept)}
                  onMouseLeave={onChapterLeave}
                  $fontWeight={
                    selectedItem === index ? Fonts.SEMIBOLD : Fonts.LIGHT
                  }
                  $cursor={educatorCoveredConcept ? 'pointer' : 'default'}
                >
                  {title}
                </StyP1>
              );
            })}
          </ChaptersList>
        </div>
        {areMoreCoursesAvailable &&
          !isSyllabusPageEducatorSpecific &&
          renderCoursesNavItem()}
      </InnerWrapper>
      <StyPopper ref={popupRef} />
    </Wrapper>
  );
};

export default SideNav;
